import { useIsAuthenticated } from '@azure/msal-react';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/contexts/user/UserContext';
import { DATE_FORMAT } from '@/lib/constants';
import { useNumberFormatting } from './numberFormatting';

export const formatNumber = (number: number, digits: number = 2) => {
    return (Math.round(number * 100) / 100).toFixed(digits);
};

export const toDecimal = (number?: number, digits: number = 2) => {
    return number !== undefined ? formatNumber(number, digits) : number;
};

export const convertStringInputToNumber = (value: string | null) => {
    let decimalValue: number | null = 0;
    if (value === '' || value === null) {
        decimalValue = null;
    } else {
        if (Number.isNaN(Number(value))) {
            return;
        }
        decimalValue = Number(value);
    }
    return decimalValue;
};

export const getCurrencySymbol = (currency: string) => {
    switch (currency) {
        case 'DKK':
            return 'kr.';
        default:
            return 'kr.';
    }
};

export const hoursOrMinutes = (hours: number) => {
    if (hours >= 1) {
        return toDecimal(hours, 1) + ' hours';
    } else {
        return toDecimal(60 * hours, 0) + ' minutes';
    }
};

export enum DateFormats {
    DATE_WITH_SHORT_MONTH = 'MMMM DD, YYYY'
}

export const useFormatting = () => {
    const { i18n } = useTranslation();

    const { toDecimal, getCurrencyLabel, getBalanceLabel, getBalanceLabelV2 } =
        useNumberFormatting();

    const { company } = useUser();
    const DefaultTZ = 'Europe/Copenhagen';

    useEffect(() => {
        dayjs.tz.setDefault(company?.ianaTimeZone ?? DefaultTZ);
    }, [company?.ianaTimeZone]);

    const formatDate = useCallback(
        (
            date: Dayjs | Date | string | undefined | null,
            format: string = DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR,
            isDateOnly: boolean = false
        ) => {
            if (!date) return '';

            if (isDateOnly) {
                return dayjs.utc(date).format(format);
            }

            return dayjs(date)
                .tz(company?.ianaTimeZone ?? DefaultTZ)
                .format(format);
        },
        [company?.ianaTimeZone]
    );

    const formatDateTime = useCallback(
        (dateTime: Dayjs | Date | undefined) => {
            return dayjs(dateTime)
                .tz(company?.ianaTimeZone ?? DefaultTZ)
                .format('ddd DD. MMM YYYY HH:mm');
        },
        [company?.ianaTimeZone]
    );

    const formatDateTimeToTime = useCallback(
        (dateTime: Dayjs | Date | undefined | null) => {
            return dayjs(dateTime)
                .tz(company?.ianaTimeZone ?? DefaultTZ)
                .format('HH:mm');
        },
        [company?.ianaTimeZone]
    );

    interface TwithDate {
        createdAt: string;
    }

    const getWeekLabel = (date: Date) => {
        const firstDayNumber = date.getUTCDate() - date.getDay();
        const lastDayNumber = firstDayNumber + 6;

        let firstWeekDay = new Date(date.getTime());
        firstWeekDay.setUTCDate(firstDayNumber);

        let lastWeekDay = new Date(date.getTime());
        lastWeekDay.setUTCDate(lastDayNumber);

        return `${formatDate(firstWeekDay)} - ${formatDate(lastWeekDay)}`;
    };

    interface GroupedArray<T> {
        label: string;
        data: T[];
    }

    const groupByWeek = <T extends TwithDate>(
        transactions: T[]
    ): GroupedArray<T>[] => {
        type Grouped = { [label: string]: Array<T> };
        let byweek: Grouped = {};
        transactions.forEach((transaction) => {
            const label = getWeekLabel(new Date(transaction.createdAt));
            byweek[label] = byweek[label] || [];
            byweek[label].push(transaction);
        });

        let byWeekArr: GroupedArray<T>[] = [];
        Object.keys(byweek).forEach((item) => {
            byWeekArr.push({ label: item, data: byweek[item] });
        });
        return byWeekArr;
    };

    return {
        toDecimal,
        getCurrencyLabel,
        formatDate,
        groupByWeek,
        getBalanceLabel,
        getBalanceLabelV2,
        formatDateTime,
        formatDateTimeToTime
    };
};
